// Hardcoded product catalogue for EcoBike Camping Supplies Co.
//
// This catalogue reflects a deliberate Heritage-Estate policy: we only
// list products the founder can personally vouch for. Every listed SKU
// has a documented supplier, real product photography, and passes UK
// legal compliance (EAPC / EN 15194 for e-bikes, Consumer Rights and
// the Offensive Weapons Act 2019 for bladed items).
//
// **On adding new products:**
// Do NOT re-populate this file with template/placeholder items. New
// SKUs are added only when:
//   1. A confirmed supplier and delivery chain exists.
//   2. Real (or bespoke Heritage-tone) product photography is in hand
//      — no generic Unsplash stock imagery.
//   3. Any legal requirements are wired in (age gate, EAPC disclaimer,
//      Consumer Contracts Regs 14-day cooling-off, etc.).
//
// **History (2026-02-28 audit):**
// Removed 3 unconfirmed EAHORA models (Romeo II, Pro II, Ultra II) and
// 4 template-placeholder camping items (Oakwood 2P Tent, Gamekeeper
// Bivvy Roll, Forester Cook Set, Pheasant Down Sleeping Bag). See
// `/app/memory/PRD.md` for the audit rationale.

export const EBIKES = [
  {
    // Slug realigned 2026-03-01 to reflect the real product: the 2026
    // Eahora Juliet in green (single-motor 90 N·m, 48V 60Ah). The
    // previous "Juliet 2 Dual Motor" listing was replaced at the
    // founder's request — Eahora don't do the Juliet II in green, only
    // the 2026 Juliet does. Backend PRODUCTS key + test EXPECTED map
    // updated in lockstep.
    slug: 'eahora-juliet-green',
    name: 'EAHORA 2026 Juliet \u2014 Green',
    subtitle: 'The Highland workhorse',
    price: '\u00a31,799',
    badge: 'Highland Workhorse',
    blurb:
      'Our flagship. A satin sage-green step-through frame that sits well against the heather. A high-torque 90 N\u00b7m rear-hub motor that pulls you up a Perthshire fire-road without breaking a sweat. A proper 48V 60Ah EV-grade battery pack delivering up to 193 km of pedal-assisted range on a single charge \u2014 enough for a full weekend loop up the glen and back. Twin-leg kickstand that holds on the softest ground with a full pannier load, dual coil suspension that shrugs off the rough stuff, and a folding step-through frame so you can swing on and off in full waterproofs. Built for anyone who rides in real weather.',
    spec: [
      '90 N\u00b7m geared hub motor (EAPC / UK: 250W, 15.5 mph)',
      'EN 15194 certified',
      '48V 60Ah  \u00b7  2.88 kWh EV-grade battery',
      '125 \u2013 193 km range per charge',
      '20\u201d \u00d7 4.0\u201d CST BFT fat tyres',
      'Step-through frame, dual coil suspension',
      'Shimano 7-speed, 4\u201d colour LCD display',
      '2,000-lumen headlight, 60lb rear cargo rack',
      '5 PAS modes, throttle, auto-cruise, 6 kph walk mode',
    ],
    disclaimer: {
      label: 'EAPC compliance',
      text: 'Supplied as a UK EAPC-compliant e-bike (250W nominal, pedal-assist, 15.5 mph cut-off) with EN 15194 certification. Rider is responsible for wearing appropriate safety kit and obeying road traffic law. Any de-restriction voids the warranty and makes the bike illegal for road use.',
    },
    adaptiveTags: ['step-through', 'low-standover'],
    // Real Eahora product photography (2026 Juliet Green). Hosted on
    // the Eahora EU CDN so the founder always sees the current shot
    // \u2014 no need to sync files. juliet_green_2.png is the classic
    // side-profile catalogue shot.
    image: 'https://eu.eahoraebike.com/cdn/shop/files/juliet_green_2.png?v=1750648605&width=2000',
    gallery: [
      {
        src: 'https://eu.eahoraebike.com/cdn/shop/files/juliet_green_2.png?v=1750648605&width=2000',
        alt: 'EAHORA 2026 Juliet Green \u2014 side profile, satin sage-green step-through frame with 20-inch fat tyres, rear cargo rack, and 4-inch colour LCD cockpit.',
        label: 'Side profile',
      },
      {
        src: 'https://eu.eahoraebike.com/cdn/shop/files/juliet_green_3.png?v=1750679242&width=2000',
        alt: 'EAHORA 2026 Juliet Green \u2014 three-quarter front view showing the 2,000-lumen headlight, dual coil suspension fork and integrated battery.',
        label: 'Front 3/4',
      },
      {
        src: 'https://eu.eahoraebike.com/cdn/shop/files/juliet_green_1.png?v=1750679242&width=2000',
        alt: 'EAHORA 2026 Juliet Green \u2014 three-quarter rear-and-side view showing the cargo rack, rear coil suspension and 20-inch fat tyres.',
        label: 'Rear + rack',
      },
    ],
    completeKit: {
      heroImage: '/hero-and-juliet/juliet-complete-kit-green.png',
      heroAlt: 'The 2026 Juliet in green leaning against a dry-stone dyke with heritage camping gear laid out on heather beside it \u2014 fire steel, bushcraft knife, enamel mug and waxed map case.',
      headline: 'Complete Kit \u2014 bike + bothy bag',
      lead:
        "The bike is only half the story. The kit laid out beside it \u2014 fire steel, Gamekeeper\u2019s bushcraft knife, waxed cotton map case, an enamel mug \u2014 is what turns a ride into a proper day out. Every piece below can be added to your order at checkout.",
      companions: [
        'heritage-fire-starter-kit',
        'heritage-field-knife',
        'heritage-estate-fire-starter-hemp-tinder-kit',
      ],
    },
  },
  // ─────────────────────────────────────────────────────────────────────
  // Enquire-only Eahora range — added 2026-03-05.
  //
  // Each of the six models below is listed with the manufacturer's own
  // plain-background product shot straight off the Eahora EU CDN, at
  // 1:1 £ = EUR MSRP (matching how the Juliet Green is priced). All
  // six are flagged `enquireOnly: true` because we can't yet confirm
  // per-SKU UK EAPC compliance (250 W nominal, 15.5 mph pedal-assist
  // cut-off, no active throttle above walking pace, EN 15194 cert).
  // The safety net is double-layered:
  //   1. UI: ProductGrid hides the Buy Now button when `enquireOnly`
  //      is truthy — customers can only route through the enquiry form.
  //   2. Backend: `backend/payments.py` PRODUCTS map does NOT list
  //      these slugs, so if any of these ever hit /api/checkout/session
  //      the server returns 400 "Unknown product". Belt AND braces.
  // Once EAHORA confirm a SKU is UK-compliant, remove `enquireOnly`
  // from that entry and add the slug to payments.py in one edit.
  // ─────────────────────────────────────────────────────────────────────
  {
    slug: 'eahora-emars-m1p',
    name: 'EAHORA Emars M1P',
    subtitle: 'Step-through fat-tyre \u2014 5 colours',
    price: '\u00a31,799 (or Enquire)',
    badge: 'Eahora \u00b7 Enquire',
    blurb:
      "Sister to the Juliet \u2014 the same easy-on / easy-off step-through frame, fat tyres for soft ground and a wider colour range. Choose from aquamarine, black, matte green, orange or red. Rated 5.0\u2605 by early riders on Eahora's own catalogue.",
    spec: [
      'Step-through fat-tyre frame',
      '5 colours: aquamarine \u00b7 black \u00b7 matte green \u00b7 orange \u00b7 red',
      'UK EAPC compliance: confirming per colour with Eahora',
      'Full specifications on request',
    ],
    disclaimer: {
      label: 'Enquire before purchase',
      text: 'We\u2019re listing this bike with the manufacturer\u2019s plain product shot and MSRP. Before we take any order, we confirm with Eahora that the chosen SKU ships as a UK EAPC-compliant e-bike (250 W nominal, 15.5 mph pedal-assist cut-off, EN 15194). Enquire below and we\u2019ll come back to you within one working day with the current UK-legal build.',
    },
    enquireOnly: true,
    image: 'https://eu.eahoraebike.com/cdn/shop/files/M1P_MATTE_GREEN-1.png?v=1780047891&width=2000',
    colourVariants: [
      { label: 'Aquamarine', image: 'https://eu.eahoraebike.com/cdn/shop/files/M1PAquamarine2.png?v=1778052785&width=2000' },
      { label: 'Black',      image: 'https://eu.eahoraebike.com/cdn/shop/files/black_M1P_upgrade_2_72c519c1-6f59-4a64-a3fa-7e5a4226730d.png?v=1777011609&width=2000' },
      { label: 'Matte Green',image: 'https://eu.eahoraebike.com/cdn/shop/files/M1P_MATTE_GREEN-1.png?v=1780047891&width=2000' },
      { label: 'Orange',     image: 'https://eu.eahoraebike.com/cdn/shop/files/Orange_M1P_upgrade_2.png?v=1778053314&width=2000' },
      { label: 'Red',        image: 'https://eu.eahoraebike.com/cdn/shop/files/M1PRed_568229c2-130c-4e8f-95a6-a553e37395b0.png?v=1778051686&width=2000' },
    ],
  },
  {
    slug: 'eahora-romeo-pro',
    name: 'EAHORA Romeo Pro',
    subtitle: 'Cargo / utility crossbar \u2014 4 colours',
    price: '\u00a31,999 (or Enquire)',
    badge: 'Eahora \u00b7 Enquire',
    blurb:
      "A workhorse frame with a proper cargo lean, made for longer runs \u2014 estate loops, coastal paths, or a full day\u2019s shopping down the glen. Rated 4.9\u2605 across nearly fifty riders on Eahora\u2019s own catalogue. Black, blue, matte green or gray.",
    spec: [
      'Crossbar cargo / utility frame',
      '4 colours: black \u00b7 blue \u00b7 green \u00b7 gray',
      'UK EAPC compliance: confirming per colour with Eahora',
      'Full specifications on request',
    ],
    disclaimer: {
      label: 'Enquire before purchase',
      text: 'We\u2019re listing this bike with the manufacturer\u2019s plain product shot and MSRP. Before we take any order, we confirm with Eahora that the chosen SKU ships as a UK EAPC-compliant e-bike (250 W nominal, 15.5 mph pedal-assist cut-off, EN 15194). Enquire below and we\u2019ll come back to you within one working day with the current UK-legal build.',
    },
    enquireOnly: true,
    image: 'https://eu.eahoraebike.com/cdn/shop/files/romeo_pro_main3.png?v=1749208987&width=2000',
    colourVariants: [
      { label: 'Black', image: 'https://eu.eahoraebike.com/cdn/shop/files/romeo_pro_main3.png?v=1749208987&width=2000' },
      { label: 'Blue',  image: 'https://eu.eahoraebike.com/cdn/shop/files/romeo_pro_blue-4.png?v=1787651717&width=2000' },
      { label: 'Green', image: 'https://eu.eahoraebike.com/cdn/shop/files/romeo_pro_green-1.png?v=1787651717&width=2000' },
      { label: 'Gray',  image: 'https://eu.eahoraebike.com/cdn/shop/files/romeo_pro_gray_1_5aceb532-e03b-4399-a8a5-6bb661d70c29.png?v=1750144449&width=2000' },
    ],
  },
  {
    slug: 'eahora-romeo-pro-ii-2026',
    name: 'EAHORA Romeo Pro II \u2014 2026',
    subtitle: 'Upgraded cargo flagship \u2014 4 colours',
    price: '\u00a32,899 (or Enquire)',
    badge: 'Eahora \u00b7 Enquire',
    blurb:
      "The 2026 upgrade to the Romeo Pro \u2014 longer-range battery, more cockpit tech, the same lift-your-shopping frame. Our next tier up when the Emars class won\u2019t reach your ground. Black, blue, gray or green.",
    spec: [
      '2026 upgraded cargo / utility frame',
      '4 colours: black \u00b7 blue \u00b7 gray \u00b7 green',
      'UK EAPC compliance: confirming per colour with Eahora',
      'Full specifications on request',
    ],
    disclaimer: {
      label: 'Enquire before purchase',
      text: 'We\u2019re listing this bike with the manufacturer\u2019s plain product shot and MSRP. Before we take any order, we confirm with Eahora that the chosen SKU ships as a UK EAPC-compliant e-bike (250 W nominal, 15.5 mph pedal-assist cut-off, EN 15194). Enquire below and we\u2019ll come back to you within one working day with the current UK-legal build.',
    },
    enquireOnly: true,
    image: 'https://eu.eahoraebike.com/cdn/shop/files/eahora_romeo_pro_II_2025_electric_bike_green_1.png?v=1750130062&width=2000',
    colourVariants: [
      { label: 'Black', image: 'https://eu.eahoraebike.com/cdn/shop/files/eahora_romeo_pro_II_2025_electric_bike_black_1.png?v=1750130062&width=2000' },
      { label: 'Gray',  image: 'https://eu.eahoraebike.com/cdn/shop/files/eahora_romeo_pro_II_2025_electric_bike_gray_1.png?v=1750130062&width=2000' },
      { label: 'Green', image: 'https://eu.eahoraebike.com/cdn/shop/files/eahora_romeo_pro_II_2025_electric_bike_green_1.png?v=1750130062&width=2000' },
      { label: 'Blue',  image: 'https://eu.eahoraebike.com/cdn/shop/files/romeo_pro_II_blue-2.png?v=1787651755&width=2000' },
    ],
  },
  {
    slug: 'eahora-terra-ii',
    name: 'EAHORA Terra II',
    subtitle: 'Fat-tyre urban \u2014 3 colours \u00b7 on sale',
    price: '\u00a31,099 (was \u00a31,799, or Enquire)',
    badge: 'Eahora \u00b7 Enquire',
    blurb:
      "Fat-tyre urban runabout, currently on Eahora\u2019s own clearance. Fair budget entry point for a first e-bike. Available in blue or green \u2014 black is sold out at source. Enquire and we\u2019ll check current stock before quoting.",
    spec: [
      'Fat-tyre urban runabout frame',
      '3 colours: blue \u00b7 green (black sold out at Eahora)',
      'UK EAPC compliance: confirming per colour with Eahora',
      'Sale price \u2014 subject to Eahora stock',
    ],
    disclaimer: {
      label: 'Enquire before purchase',
      text: 'We\u2019re listing this bike with the manufacturer\u2019s plain product shot and MSRP. Before we take any order, we confirm with Eahora that the chosen SKU ships as a UK EAPC-compliant e-bike (250 W nominal, 15.5 mph pedal-assist cut-off, EN 15194). Enquire below and we\u2019ll come back to you within one working day with the current UK-legal build.',
    },
    enquireOnly: true,
    image: 'https://eu.eahoraebike.com/cdn/shop/files/terra2_blue_right_2736fde8-52bf-4e93-8c6e-a1f008522ee3.png?v=1751100266&width=2000',
    colourVariants: [
      { label: 'Blue',  image: 'https://eu.eahoraebike.com/cdn/shop/files/terra2_blue_right_2736fde8-52bf-4e93-8c6e-a1f008522ee3.png?v=1751100266&width=2000' },
      { label: 'Green', image: 'https://eu.eahoraebike.com/cdn/shop/files/terra2_green_right_d502bb70-682b-40a5-bccc-01a343d78e0d.png?v=1751100204&width=2000' },
    ],
  },
  {
    slug: 'eahora-luna-ii',
    name: 'EAHORA Luna II',
    subtitle: 'Compact fat-tyre commuter \u00b7 on sale',
    price: '\u00a31,099 (was \u00a31,699, or Enquire)',
    badge: 'Eahora \u00b7 Enquire',
    blurb:
      "Compact fat-tyre commuter. On sale at Eahora \u2014 good ground for a second bike or a first e-bike at a fair price. Black or white.",
    spec: [
      'Compact fat-tyre commuter frame',
      '2 colours: black \u00b7 white',
      'UK EAPC compliance: confirming per colour with Eahora',
      'Sale price \u2014 subject to Eahora stock',
    ],
    disclaimer: {
      label: 'Enquire before purchase',
      text: 'We\u2019re listing this bike with the manufacturer\u2019s plain product shot and MSRP. Before we take any order, we confirm with Eahora that the chosen SKU ships as a UK EAPC-compliant e-bike (250 W nominal, 15.5 mph pedal-assist cut-off, EN 15194). Enquire below and we\u2019ll come back to you within one working day with the current UK-legal build.',
    },
    enquireOnly: true,
    image: 'https://eu.eahoraebike.com/cdn/shop/files/luna2_black_right_14cf4fdb-468b-498b-8ac4-aec591f60b8b.png?v=1751095076&width=2000',
    colourVariants: [
      { label: 'Black', image: 'https://eu.eahoraebike.com/cdn/shop/files/luna2_black_right_14cf4fdb-468b-498b-8ac4-aec591f60b8b.png?v=1751095076&width=2000' },
      { label: 'White', image: 'https://eu.eahoraebike.com/cdn/shop/files/luna2_white_right_a552c5f0-15db-428d-b0aa-2ad3a2a89849.png?v=1751096765&width=2000' },
    ],
  },
  {
    slug: 'eahora-bad-boy-m8s',
    name: 'EAHORA Bad Boy M8S',
    subtitle: 'Full-suspension all-terrain \u00b7 pre-order',
    price: '\u00a32,899 (pre-order or Enquire)',
    badge: 'Eahora \u00b7 Enquire',
    blurb:
      "Full-suspension all-terrain \u2014 the pre-order flagship for the more adventurous rider. Rear shock and heavier tyres. This is the bike for actual off-road ground, not tow-paths. Pre-order at Eahora \u2014 enquire for current ETA and UK build spec.",
    spec: [
      'Full-suspension all-terrain frame',
      'Rear shock, heavier off-road tyres',
      'UK EAPC compliance: confirming with Eahora',
      'Pre-order status \u2014 enquire for lead time',
    ],
    disclaimer: {
      label: 'Enquire before purchase',
      text: 'We\u2019re listing this bike with the manufacturer\u2019s plain product shot and MSRP. Before we take any order, we confirm with Eahora that the chosen SKU ships as a UK EAPC-compliant e-bike (250 W nominal, 15.5 mph pedal-assist cut-off, EN 15194). Enquire below and we\u2019ll come back to you within one working day with the current UK-legal build and pre-order lead time.',
    },
    enquireOnly: true,
    image: 'https://eu.eahoraebike.com/cdn/shop/files/M8S_productpage_black_2.png?v=1780050983&width=2000',
  },
];

// ─────────────────────────────────────────────────────────────────────
// Heritage Estate collection — added 2026-03-01.
// 11 fire starter & survival products sourced from Baiyuheng (all
// confirmed supplier, all UK-legal, none age-restricted). Grouped
// under the /heritage-estate collection page and rendered here so
// they also flow through the standard camping-style checkout logic.
// Slug format: heritage-estate-<descriptor>. SKU (HE-FS-##) kept
// alongside so The Bothy Orders panel can reference it verbatim.
// ─────────────────────────────────────────────────────────────────────
// Honest placeholder policy (2026-03-01, corrected)
// ─────────────────────────────────────────────────────────────────────
// The Nano-Banana-generated product shots for the Heritage Estate
// range, Heritage Field Knife and Heritage Fire Starter Kit have been
// removed because they depicted BAIN laser-engraving and brass badges
// that are NOT physically confirmed on the drop-shipped supplier
// stock. Under UK Consumer Rights Act 2015 we may not show branding
// or finish that the customer will not actually receive.
//
// Until real supplier product photography arrives, every affected
// SKU renders a neutral parchment card that reads "Photograph
// pending — real supplier photography arriving soon." Archived
// originals live at /app/_archived_ai_generated/ (outside the web
// root) in case any of that stock ends up genuinely branded and we
// need to reinstate a specific shot with the founder's sign-off.
// ─────────────────────────────────────────────────────────────────────
const HE_PLACEHOLDER_IMG = '/products/photo-pending.png';

// Every Heritage Estate SKU + bundle now points at the same honest
// placeholder. When real supplier photography arrives, replace the
// individual mapping — do NOT restore any Nano-Banana shot without
// explicit founder sign-off that the physical stock is branded to
// match.
const HE_IMAGES = {
  'heritage-estate-fire-starter-waxed-jute-kit': HE_PLACEHOLDER_IMG,
  'heritage-estate-fire-starter-hemp-tinder-kit': HE_PLACEHOLDER_IMG,
  'heritage-estate-jumbo-fire-steel-paracord-grip': HE_PLACEHOLDER_IMG,
  'heritage-estate-10-in-1-fire-starter-kit': HE_PLACEHOLDER_IMG,
  'heritage-estate-navigator-fire-steel': HE_PLACEHOLDER_IMG,
  'heritage-estate-6-in-1-compact-survival-kit': HE_PLACEHOLDER_IMG,
  'heritage-estate-endurance-fire-steel': HE_PLACEHOLDER_IMG,
  'heritage-estate-hex-block-fire-steel': HE_PLACEHOLDER_IMG,
  'heritage-estate-edc-fire-steel-necklace': HE_PLACEHOLDER_IMG,
  'heritage-estate-survival-toggle-ferro-rod': HE_PLACEHOLDER_IMG,
  'heritage-estate-heavy-duty-fire-steel-blank': HE_PLACEHOLDER_IMG,
  'highland-fire-kit-bundle': HE_PLACEHOLDER_IMG,
  'bothy-weekend-kit-bundle': HE_PLACEHOLDER_IMG,
  'deep-winter-kit-bundle': HE_PLACEHOLDER_IMG,
  'starter-kit-bundle': HE_PLACEHOLDER_IMG,
};

export const HERITAGE_ESTATE = [
  {
    slug: 'heritage-estate-fire-starter-waxed-jute-kit',
    sku: 'HE-FS-08',
    category: 'Fire Starters',
    name: 'Heritage Estate Fire Starter — Waxed Jute Kit',
    subtitle: 'All-weather flagship · BAIN engraved',
    price: '£59.95',
    badge: 'Flagship',
    featured: true,
    summary:
      "A proper fire starter with natural wood handle and waxed jute tinder — the way it's been done on Highland estates for generations. Built to last 12,000 strikes in any weather.",
    blurb:
      "This is the fire starter we'd have wanted on the hill twenty years ago. A full-length 120mm ferrocerium rod set in a natural wood handle that takes the Heritage Estate brass badge beautifully — laser-engraved with BAIN, because four generations on this land means something. Comes with waxed jute tinder rope and a high-carbon steel striker. Throws 3,000°C sparks in rain, sleet, or a January gale. No lighter fluid, no fumbling with matches in wet gloves. Just honest function built to outlast you.",
    spec: [
      '120mm ferrocerium rod — 12,000+ strikes guaranteed',
      'Natural wood handle with Heritage Estate brass badge & BAIN laser engraving',
      'Waxed jute tinder rope included (10 x 100mm)',
      'High-carbon steel striker — 3,000°C spark temperature',
      'Waterproof — works in rain, snow, and gale-force conditions',
      'Premium kraft presentation box with Heritage Estate branding',
    ],
    image: HE_IMAGES['heritage-estate-fire-starter-waxed-jute-kit'],
  },
  {
    slug: 'heritage-estate-fire-starter-hemp-tinder-kit',
    sku: 'HE-FS-09',
    category: 'Fire Starters',
    name: 'Heritage Estate Fire Starter — Hemp Tinder Kit',
    subtitle: 'Flagship · Laser-engraved wood handle',
    price: '£64.95',
    badge: 'Flagship',
    featured: true,
    summary:
      'Our flagship wood-handled fire steel with 35cm hemp tinder rope. Laser-engraved Heritage Estate handle — the centrepiece of the range.',
    blurb:
      "The definitive Heritage Estate fire starter. Ergonomic natural wood handle designed to sit right in the hand, even with cold fingers after a long stalk. The 5/16\" ferrocerium rod delivers 12,000+ strikes at 3,000°C, and the included 35cm hemp tinder rope catches first time — no messing about. The wooden handle is the hero piece: laser-engraved with the Heritage Estate mark and BAIN family name. This isn't decorative kit that lives in a drawer. It's working gear that happens to look the part.",
    spec: [
      '5/16" x 3" (8mm x 76mm) ferrocerium rod — 12,000+ strikes',
      'Ergonomic natural wood handle with Heritage Estate laser engraving',
      '35cm (13.8") hemp tinder rope included',
      'High-carbon steel striker — all-weather 3,000°C sparks',
      'Waterproof construction — functions in any conditions',
      'Heritage Estate brass badge with BAIN prominently displayed',
    ],
    image: HE_IMAGES['heritage-estate-fire-starter-hemp-tinder-kit'],
  },
  {
    slug: 'heritage-estate-jumbo-fire-steel-paracord-grip',
    sku: 'HE-FS-04',
    category: 'Fire Starters',
    name: 'Heritage Estate Jumbo Fire Steel — Paracord Grip',
    subtitle: '127mm rod · 15,000 strikes',
    price: '£54.95',
    summary:
      'Oversized 5-inch ferro rod wrapped in braided paracord with multi-tool striker. 15,000 strikes of absolute reliability when conditions turn ugly.',
    blurb:
      "When you need a fire and the weather's doing its worst — horizontal rain, fingers numb, daylight fading — this is the rod you reach for. A full 127mm of ferrocerium wrapped in braided paracord that doubles as emergency cordage. The multi-tool striker throws fat, angry sparks at 3,000°C. At 15,000+ strikes, your grandchildren will inherit it. Oversized by design: easier to grip with cold hands, harder to lose in the dark.",
    spec: [
      '127mm x 12.7mm oversized ferrocerium rod — 15,000+ strikes',
      'Braided paracord handle grip (emergency cordage)',
      'Multi-tool high-carbon steel striker',
      '3,000°C spark temperature — lights in any weather',
      'Heritage Estate branded presentation packaging',
      'Oversized for easier cold-weather grip',
    ],
    image: HE_IMAGES['heritage-estate-jumbo-fire-steel-paracord-grip'],
  },
  {
    slug: 'heritage-estate-10-in-1-fire-starter-kit',
    sku: 'HE-FS-12',
    category: 'Survival Gear',
    name: 'Heritage Estate 10-in-1 Fire Starter Kit',
    subtitle: 'Three tinder types · 12,000 strikes',
    price: '£74.95',
    summary:
      'The complete fire-lighting solution. Ten components including ferro rod, multiple tinder types, and paracord wrap — everything needed to get a flame in any conditions.',
    blurb:
      "We put this kit together for the folk who want to walk out the door with everything they need to make fire, no matter what the hill throws at them. Paracord-wrapped 127mm ferro rod, hemp tinder, waxed jute, and wooden tinder — three ways to catch a spark. High-carbon steel striker delivers 3,000°C reliably across 12,000+ uses. It's the kit we'd hand to someone heading out on their first overnight wild camp, and the one we'd grab ourselves heading up the glen in February.",
    spec: [
      '10-in-1 comprehensive fire-starting kit',
      '127mm x 12.7mm paracord-wrapped ferrocerium rod',
      'Three tinder types included: hemp, waxed jute, and wood',
      'High-carbon steel striker — 12,000+ strikes at 3,000°C',
      'Waterproof in all conditions',
      'Heritage Estate branded kraft presentation box',
    ],
    image: HE_IMAGES['heritage-estate-10-in-1-fire-starter-kit'],
  },
  {
    slug: 'heritage-estate-navigator-fire-steel',
    sku: 'HE-FS-03',
    category: 'Survival Gear',
    name: 'Heritage Estate Navigator Fire Steel',
    subtitle: 'Ferro rod + integrated compass',
    price: '£44.95',
    summary:
      'Ferro rod with integrated compass in a knurled aluminium handle. Fire and direction in one pocket-sized tool — built for the hill.',
    blurb:
      "Two essentials in one: a reliable 8mm x 12cm ferro rod paired with an integrated compass, all housed in a knurled aluminium handle that won't slip even when wet. The compass sits in the handle cap — always there, never needs batteries, never runs out. The high-carbon steel striker throws 3,000°C sparks for 12,000+ uses. Waterproof and built tough enough for daily carry on the estate or a week in the backcountry. When you're navigating and need a fire, you want fewer things to lose, not more.",
    spec: [
      '8mm x 12cm ferrocerium rod — 12,000+ strikes',
      'Integrated compass in handle cap',
      'Knurled aluminium handle — secure wet-weather grip',
      '20mm x 75mm high-carbon steel striker',
      'Waterproof and all-weather reliable',
      'Compact enough for EDC or jacket pocket',
    ],
    image: HE_IMAGES['heritage-estate-navigator-fire-steel'],
  },
  {
    slug: 'heritage-estate-6-in-1-compact-survival-kit',
    sku: 'HE-FS-10',
    category: 'Survival Gear',
    name: 'Heritage Estate 6-in-1 Compact Survival Kit',
    subtitle: 'Multi-tool striker · Pocket-friendly',
    price: '£44.95',
    summary:
      "Pocket-sized 6-in-1 survival fire kit with multi-tool striker and tinder rope. Everything you need, nothing you don't.",
    blurb:
      "Compact doesn't mean compromised. This kit packs a precision 8x80mm ferro rod, a 6-in-1 multi-tool striker, and waxed jute tinder into a package that disappears into a jacket pocket or rucksack side pouch. The multi-tool striker handles more than just sparking — bottle opener, hex wrench, ruler markings, and screwdriver built in. Perfect for day hikes, fishing trips, or as the backup fire source you always carry. 12,000+ strikes at 3,000°C means it'll be working long after you've forgotten you bought it.",
    spec: [
      '8x80mm precision ferrocerium rod — 12,000+ strikes',
      '6-in-1 multi-tool striker (scraper, bottle opener, hex wrench, ruler, screwdriver)',
      'Waxed jute tinder rope included',
      'Compact pocket-friendly size',
      '3,000°C spark temperature — all-weather ignition',
      'Heritage Estate branded packaging',
    ],
    image: HE_IMAGES['heritage-estate-6-in-1-compact-survival-kit'],
  },
  {
    slug: 'heritage-estate-endurance-fire-steel',
    sku: 'HE-FS-11',
    category: 'Fire Starters',
    name: 'Heritage Estate Endurance Fire Steel',
    subtitle: '20,000+ strikes · Bare rod',
    price: '£42.95',
    summary:
      'Our longest-lasting bare ferro rod — 20,000+ strikes with HSS scraper and 12-inch waxed jute tinder. For folk who measure fire starters in decades, not seasons.',
    blurb:
      "Twenty thousand strikes. That's a fire every night for over fifty years. This 5-inch, half-inch-diameter ferrocerium rod is built for longevity — the kind of tool you buy once. Comes with a 4-inch HSS (high-speed steel) scraper that throws monster sparks and a generous 12-inch length of waxed jute tinder. No handle — this is a purist's rod, bare and functional. Pair it with your own handle build or carry it raw. Either way, it'll outlast everything else in your pack.",
    spec: [
      '127mm x 12.7mm ferrocerium rod — 20,000+ strikes',
      '4-inch HSS (high-speed steel) scraper — superior spark production',
      '12-inch (304mm) waxed jute tinder rope included',
      '3,000°C spark temperature in all conditions',
      'Waterproof bare rod — no handle to rot or break',
      'Lifetime-grade durability',
    ],
    image: HE_IMAGES['heritage-estate-endurance-fire-steel'],
  },
  {
    slug: 'heritage-estate-hex-block-fire-steel',
    sku: 'HE-FS-05',
    category: 'Fire Starters',
    name: 'Heritage Estate Hex Block Fire Steel',
    subtitle: '30mm hex · Anti-roll',
    price: '£49.95',
    summary:
      "Massive 30mm hexagonal ferro rod — won't roll off a rock, won't run out in your lifetime. 20,000+ strikes of brute reliability.",
    blurb:
      "This is the rod for base camp, the bothy shelf, or the man who wants a fire starter that feels like it means business. Thirty millimetres of solid ferrocerium in a hexagonal profile that stays put on any surface. The hex shape isn't just clever engineering — it gives you a flat striking face every time. Twenty thousand-plus strikes at 3,000°C. It's not subtle, it's not lightweight, and it doesn't pretend to be. It's the fire steel equivalent of a Land Rover Defender: overbuilt, reliable, and quietly impressive.",
    spec: [
      '30mm x 125mm solid ferrocerium — 20,000+ strikes',
      'Hexagonal anti-roll design — stays where you put it',
      'Flat striking faces for consistent spark production',
      '3,000°C spark temperature — all conditions',
      '30mm x 90mm high-carbon steel striker included',
      'Waterproof — OEM-ready for Heritage Estate laser branding',
    ],
    image: HE_IMAGES['heritage-estate-hex-block-fire-steel'],
  },
  {
    slug: 'heritage-estate-edc-fire-steel-necklace',
    sku: 'HE-FS-02',
    category: 'Fire Starters',
    name: 'Heritage Estate EDC Fire Steel Necklace',
    subtitle: 'Everyday carry · Paracord lanyard',
    price: '£36.95',
    summary:
      'Compact ferro rod on a paracord necklace with scraper — fire-making capability that never leaves your person. Everyday carry, Highland style.',
    blurb:
      "The fire steel you wear, not the one you forget at the bottom of your rucksack. A compact 8mm x 30mm ferrocerium rod on a paracord lanyard with integrated scraper — sits under a shirt collar and you'll forget it's there until you need it. Twelve thousand strikes at 3,000°C. The paracord itself is emergency cordage. Whether you're on the hill daily or just want the confidence of always having fire, this is the piece that stays with you.",
    spec: [
      '8mm x 30mm compact ferrocerium rod — 12,000+ strikes',
      'Paracord necklace lanyard (emergency cordage)',
      'Integrated 27.9mm high-carbon steel scraper',
      '3,000°C spark temperature — all-weather',
      "EDC design — wear daily, forget it's there until needed",
      'Heritage Estate laser-etched branding',
    ],
    image: HE_IMAGES['heritage-estate-edc-fire-steel-necklace'],
  },
  {
    slug: 'heritage-estate-survival-toggle-ferro-rod',
    sku: 'HE-FS-01',
    category: 'Fire Starters',
    name: 'Heritage Estate Survival Toggle — Ferro Rod',
    subtitle: 'Dual-hole toggle for paracord',
    price: '£12.95',
    summary:
      'Dual-hole 25mm ferro rod toggle — thread it into paracord bracelets, lanyards, or gear loops. Fire-starting capability built into whatever you\u2019re already carrying.',
    blurb:
      "A 25mm ferrocerium toggle with dual holes for threading into paracord projects, zipper pulls, or survival bracelets. Small enough to integrate anywhere, powerful enough to deliver 3,000°C sparks for 12,000+ strikes. This isn't a standalone fire starter — it's a fire-starting component you weave into existing gear. Perfect for building custom survival bracelets or adding fire capability to a jacket, bag, or belt. The folk who carry these tend to carry several.",
    spec: [
      '25mm (1-inch) ferrocerium toggle — 12,000+ strikes',
      'Dual-hole design for paracord threading',
      '3,000°C spark temperature',
      '8mm rod diameter — compact but capable',
      'Integrates into bracelets, lanyards, zipper pulls',
      'Sold individually — buy multiples for project builds',
    ],
    image: HE_IMAGES['heritage-estate-survival-toggle-ferro-rod'],
  },
  {
    slug: 'heritage-estate-heavy-duty-fire-steel-blank',
    sku: 'HE-FS-06',
    category: 'Fire Starters',
    name: 'Heritage Estate Heavy Duty Fire Steel Blank',
    subtitle: '25.5cm bare blank · For makers',
    price: '£29.95',
    summary:
      'The longest ferro rod in the range — 25.5cm of solid fire steel for custom builds, knife spine integration, or bushcraft projects.',
    blurb:
      "For the maker and the bushcrafter who builds their own kit. A full 25.5cm bare ferrocerium blank — cut it, shape it, integrate it into a knife handle or custom fire starter build. Twelve thousand strikes at 3,000°C from a rod that can be sectioned into multiple fire steels or used whole as the ultimate long-lasting backup. No handle, no frills — just raw material for people who know exactly what they want to do with it.",
    spec: [
      '25.5cm (10-inch) bare ferrocerium blank',
      '10mm diameter — substantial and durable',
      '12,000+ strikes at 3,000°C',
      'Cut-to-size for custom projects',
      'Waterproof ferrocerium — no degradation in storage',
      'Ideal for knife spine integration or custom handle builds',
    ],
    image: HE_IMAGES['heritage-estate-heavy-duty-fire-steel-blank'],
  },
];

// ─────────────────────────────────────────────────────────────────────
// Heritage Estate curated bundle — added 2026-03-01. One Stripe SKU
// per bundle. Fulfilment ships all bundled SKUs together. The bundle
// price is the "amount" registered in backend/payments.py PRODUCTS.
// ─────────────────────────────────────────────────────────────────────
export const HERITAGE_BUNDLES = [
  {
    slug: 'starter-kit-bundle',
    sku: 'HE-BUNDLE-04',
    name: 'The Starter',
    subtitle: 'Sub-£50 entry kit',
    price: '£44.95',
    rrp: '£49.90',
    saving: 'Save £4.95 (10% off)',
    badge: 'Bundle',
    blurb:
      "The wee starter kit: the EDC Fire Steel Necklace so you carry a spark on your person, and the Survival Toggle to weave into a bracelet, zipper or bergen. Two pieces, one under-£50 price, and you\u2019re never without fire — Highland style.",
    includes: [
      { slug: 'heritage-estate-edc-fire-steel-necklace', name: 'EDC Fire Steel Necklace', price: '£36.95' },
      { slug: 'heritage-estate-survival-toggle-ferro-rod', name: 'Survival Toggle (Ferro Rod)', price: '£12.95' },
    ],
    image: HE_IMAGES['starter-kit-bundle'],
  },
  {
    slug: 'highland-fire-kit-bundle',
    sku: 'HE-BUNDLE-01',
    name: 'Highland Fire Kit',
    subtitle: 'Three-piece flagship bundle',
    price: '£94.95',
    rrp: '£115.85',
    saving: 'Save £20.90 (18% off)',
    badge: 'Bundle',
    blurb:
      "Our three-piece Highland Fire Kit: the flagship Waxed Jute Fire Starter for real weather, the Endurance Fire Steel for a lifetime of use, and the EDC Survival Toggle so you're never without a spark. Buy all three together and save £20.90 on the individual prices.",
    includes: [
      { slug: 'heritage-estate-fire-starter-waxed-jute-kit', name: 'Waxed Jute Fire Starter Kit', price: '£59.95' },
      { slug: 'heritage-estate-endurance-fire-steel', name: 'Endurance Fire Steel', price: '£42.95' },
      { slug: 'heritage-estate-survival-toggle-ferro-rod', name: 'Survival Toggle (Ferro Rod)', price: '£12.95' },
    ],
    image: HE_IMAGES['highland-fire-kit-bundle'],
  },
  {
    slug: 'bothy-weekend-kit-bundle',
    sku: 'HE-BUNDLE-02',
    name: 'Bothy Weekend Kit',
    subtitle: 'The two-night stay kit',
    price: '£91.95',
    rrp: '£111.90',
    saving: 'Save £19.95 (18% off)',
    badge: 'Bundle',
    blurb:
      "For the weekend up the glen: the belt-worn 10-in-1 Fire Starter Kit that covers you from dropped kit to lost signal, paired with the EDC Fire Steel Necklace so you never leave the bothy without a spark on your person. Everything the two-night stay actually needs.",
    includes: [
      { slug: 'heritage-estate-10-in-1-fire-starter-kit', name: '10-in-1 Fire Starter Kit', price: '£74.95' },
      { slug: 'heritage-estate-edc-fire-steel-necklace', name: 'EDC Fire Steel Necklace', price: '£36.95' },
    ],
    image: HE_IMAGES['bothy-weekend-kit-bundle'],
  },
  {
    slug: 'deep-winter-kit-bundle',
    sku: 'HE-BUNDLE-03',
    name: 'Deep Winter Kit',
    subtitle: 'Base camp + backup',
    price: '£99.95',
    rrp: '£114.90',
    saving: 'Save £14.95 (13% off)',
    badge: 'Bundle',
    blurb:
      "For January weather when a fire isn't a nicety, it's the walk home. The Hex Block Fire Steel sits square at base camp — no rolling off a wet rock — paired with the Hemp Tinder Kit for the long, low-smoke burn once you're back inside the tent or shelter.",
    includes: [
      { slug: 'heritage-estate-hex-block-fire-steel', name: 'Hex Block Fire Steel', price: '£49.95' },
      { slug: 'heritage-estate-fire-starter-hemp-tinder-kit', name: 'Hemp Tinder Fire Starter Kit', price: '£64.95' },
    ],
    image: HE_IMAGES['deep-winter-kit-bundle'],
  },
];

export const CAMPING = [
  {
    slug: 'heritage-field-knife',
    name: 'Heritage Field Knife',
    subtitle: 'Specialised field & bushcraft tool',
    price: '\u00a319.99 (or Enquire)',
    badge: 'Heritage Estate',
    blurb:
      'A proper, honest camp knife for cutting cord, whittling tent pegs and cleaning kit at the end of a long day. A 3CR13 stainless steel blade finished with a laser-etched Damascus pattern, paired with a warm genuine olive-wood handle \u2014 the kind of tool that looks better with a decade\u2019s use on it.',
    spec: [
      '3CR13 stainless steel blade, laser-etched Damascus pattern',
      'Genuine olive wood handle',
      'Brass pin rivets',
      '220 g',
    ],
    disclaimer: {
      label: '18+ \u00b7 lawful use only',
      text: 'Sold strictly for legitimate outdoor, camping and bushcraft use. Purchaser must be 18 or over. It is the buyer\u2019s responsibility to comply with the Criminal Justice Act 1988 and the Offensive Weapons Act 2019 when carrying and using bladed articles.',
    },
    image: HE_PLACEHOLDER_IMG,
  },
  {
    slug: 'heritage-fire-starter-kit',
    name: 'Heritage Fire Starter Kit',
    subtitle: 'Ferro rod & striker',
    price: '\u00a39.99',
    badge: 'Heritage Estate',
    blurb:
      'A 115 mm ferrocerium rod set into a solid wooden handle, paired with a matching steel striker. Tested in Highland conditions: a 3,000\u00b0C spark in any weather, wet gloves, wind, sleet or shine. Every serious camper\u2019s quiet essential.',
    spec: ['115 mm ferrocerium rod', 'Solid oak handle', 'Matching steel striker', 'Ships within 14 days'],
    image: HE_PLACEHOLDER_IMG,
  },
];
